import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, IconButton, Button, CircularProgress,
  LinearProgress, Alert, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TerrainIcon from '@mui/icons-material/Terrain';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import axios from 'axios';
import api, { inspectionTypesApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// ---- On-screen A4 page geometry (px @ ~96dpi) ----
const PAGE_W = 794, PAGE_H = 1123, PAGE_PAD = 36;
const CONTENT_W = PAGE_W - 2 * PAGE_PAD;                 // 722
const CONTENT_H = PAGE_H - 2 * PAGE_PAD;                 // 1051
const NO_W = Math.round(CONTENT_W * 10 / 190);          // ~38
const ID_W = Math.round(CONTENT_W * 34 / 190);          // ~129
const REM_W = Math.round(CONTENT_W * 36 / 190);         // ~137
const PHOTOS_W = CONTENT_W - NO_W - ID_W - REM_W;       // ~418
const PHOTO_GAP = 6, CELL_PAD = 6, PCOLS = 3;
const CELL_W = Math.floor((PHOTOS_W - 2 * CELL_PAD - (PCOLS - 1) * PHOTO_GAP) / PCOLS); // ~131
const CELL_H = Math.round(CELL_W * 0.72);               // ~94
const HEADER_H = 30, SECTION_H = 24, MIN_ROW_H = 120;

const toRoman = (n) => ({ 1: 'I', 2: 'II', 3: 'III', 4: 'IV', 5: 'V', 6: 'VI', 7: 'VII', 8: 'VIII' }[n] || String(n));

const loadImage = (url) => new Promise((resolve, reject) => {
  const img = new window.Image();
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = url;
});

// Draw the photo + validated boxes/labels onto a (downscaled) canvas -> { dataUrl, w, h }
function compositePhoto(img, photo, maxW = 1000) {
  const nW = img.naturalWidth || photo.image_width || 1280;
  const nH = img.naturalHeight || photo.image_height || 960;
  const scale = nW > maxW ? maxW / nW : 1;
  const W = Math.max(1, Math.round(nW * scale));
  const H = Math.max(1, Math.round(nH * scale));
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, W, H);

  const sx = W / (photo.image_width || nW);
  const sy = H / (photo.image_height || nH);
  ctx.lineWidth = Math.max(2, Math.round(W * 0.006));
  ctx.strokeStyle = '#ff3b30';
  const fontPx = Math.max(13, Math.round(W * 0.03));
  ctx.font = `bold ${fontPx}px Arial, sans-serif`;
  ctx.textBaseline = 'alphabetic';

  (photo.boxes || []).forEach((b) => {
    if (b.x1 == null || b.y1 == null || b.x2 == null || b.y2 == null) return;
    const x = Number(b.x1) * sx, y = Number(b.y1) * sy;
    const w = (Number(b.x2) - Number(b.x1)) * sx, h = (Number(b.y2) - Number(b.y1)) * sy;
    ctx.strokeRect(x, y, w, h);
    const label = b.severity_name ? `${b.defect_type_name} · ${b.severity_name}` : (b.defect_type_name || 'Defect');
    const pad = Math.round(fontPx * 0.28);
    const tw = ctx.measureText(label).width + 2 * pad;
    const th = fontPx + 2 * pad;
    const barY = y - th >= 0 ? y - th : y + h;
    ctx.fillStyle = 'rgba(255,59,48,0.85)';
    ctx.fillRect(x, barY, tw, th);
    ctx.fillStyle = '#ffffff';
    ctx.fillText(label, x + pad, barY + th - pad - 1);
  });

  return { dataUrl: canvas.toDataURL('image/jpeg', 0.85), w: W, h: H };
}

const rowHeight = (slope) => {
  const rows = Math.max(1, Math.ceil(slope.photos.length / PCOLS));
  const photoH = rows * CELL_H + (rows - 1) * PHOTO_GAP + 2 * CELL_PAD;
  return Math.max(photoH, MIN_ROW_H);
};

// Data-driven pagination (depends only on photo counts, so editing remarks never reflows pages)
function buildPages(slopes) {
  const pages = [{ cover: true }];
  if (!slopes?.length) return pages;
  let page = { items: [] }, h = 0, lastSection = null, no = 0;
  const flush = () => { if (page.items.length) { pages.push(page); page = { items: [] }; h = 0; } };
  for (const slope of slopes) {
    no++;
    const rH = rowHeight(slope);
    let secH = 0, addSection = false;
    if (slope.section_code !== lastSection) { addSection = true; secH = SECTION_H; lastSection = slope.section_code; }
    if (page.items.length && h + secH + rH > CONTENT_H - HEADER_H) flush();
    if (addSection) page.items.push({ type: 'section', code: slope.section_code, name: slope.section_name });
    page.items.push({ type: 'slope', slope, no, rH });
    h += secH + rH;
  }
  flush();
  return pages;
}

const centerCell = { display: 'flex', alignItems: 'center', justifyContent: 'center' };

/**
 * RegionalPhotoIndexModal — renders an on-screen preview of the "Photo Index by Region"
 * report (composited photos with boxes+labels, editable remarks per slope), then a
 * "Download Report" button captures the pages to a PDF (html2canvas -> jsPDF) and saves
 * it to /reports. Data: GET /inspections/region-photo-index.
 */
const RegionalPhotoIndexModal = ({ open, onClose, region, inspectionTypeId, inspectionTypeName, cycle, year, onGenerated }) => {
  const theme = useTheme();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [remarks, setRemarks] = useState({});                 // asset_id -> text
  const [photoAssets, setPhotoAssets] = useState({});         // photo_id -> {dataUrl,w,h} | null
  const [photoLoad, setPhotoLoad] = useState({ done: 0, total: 0 });
  const [gen, setGen] = useState({ active: false, current: 0, total: 0 });
  const [error, setError] = useState(null);
  const [warn, setWarn] = useState(null);
  const reportRef = useRef(null);
  const cacheRef = useRef(new Map());

  // Fetch report data
  useEffect(() => {
    if (!open || !inspectionTypeId || !cycle || !year || !region) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null); setWarn(null);
      setData(null); setPhotoAssets({}); setPhotoLoad({ done: 0, total: 0 });
      cacheRef.current.clear();
      try {
        const res = await inspectionTypesApi.getRegionPhotoIndex({ inspection_type_id: inspectionTypeId, cycle, year, region });
        if (cancelled) return;
        setData(res.data);
        const seed = {};
        (res.data.slopes || []).forEach((s) => { seed[s.asset_id] = s.remarks || ''; });
        setRemarks(seed);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.error || e.message || 'Failed to load report data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, inspectionTypeId, cycle, year, region]);

  const resolveComposite = useCallback(async (photo) => {
    if (cacheRef.current.has(photo.photo_id)) return cacheRef.current.get(photo.photo_id);
    let objUrl = null;
    try {
      const { data: secure } = await api.post('/uploads/get-secure-url', { path: photo.photo_path });
      if (!secure?.url) throw new Error('no url');
      const resp = await axios.get(secure.url, { responseType: 'blob' });
      objUrl = URL.createObjectURL(resp.data);
      const img = await loadImage(objUrl);
      const asset = compositePhoto(img, photo);
      cacheRef.current.set(photo.photo_id, asset);
      return asset;
    } catch (e) {
      cacheRef.current.set(photo.photo_id, null);
      return null;
    } finally {
      if (objUrl) URL.revokeObjectURL(objUrl);
    }
  }, []);

  // Load + composite all photos once data arrives (progressively fills the preview)
  useEffect(() => {
    if (!data?.slopes) return;
    let cancelled = false;
    const allPhotos = data.slopes.flatMap((s) => s.photos);
    setPhotoAssets({});
    setPhotoLoad({ done: 0, total: allPhotos.length });
    (async () => {
      let done = 0, failed = 0;
      for (const p of allPhotos) {
        if (cancelled) return;
        const asset = await resolveComposite(p);
        if (cancelled) return;
        if (!asset) failed++;
        setPhotoAssets((prev) => ({ ...prev, [p.photo_id]: asset }));
        setPhotoLoad({ done: ++done, total: allPhotos.length });
      }
      if (!cancelled && failed > 0) setWarn(`${failed} photo(s) could not be loaded from storage.`);
    })();
    return () => { cancelled = true; };
  }, [data, resolveComposite]);

  const pages = useMemo(() => buildPages(data?.slopes), [data]);
  const slopeCount = data?.slopes?.length || 0;
  const photoCount = data?.photo_count || 0;
  const photosReady = photoLoad.total > 0 && photoLoad.done >= photoLoad.total;

  const handleDownload = async () => {
    if (!reportRef.current) return;
    const pageEls = reportRef.current.querySelectorAll('.pdf-page-element');
    if (!pageEls.length) return;
    setGen({ active: true, current: 0, total: pageEls.length });
    setError(null);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      for (let i = 0; i < pageEls.length; i++) {
        setGen({ active: true, current: i + 1, total: pageEls.length });
        // eslint-disable-next-line no-await-in-loop
        const canvas = await html2canvas(pageEls[i], { scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false });
        const imgData = canvas.toDataURL('image/jpeg', 0.9);
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297);
      }
      const blob = pdf.output('blob');
      const fileName = `Regional_Photo_Index_${region}_Cycle${cycle}_${year}.pdf`.replace(/[^A-Za-z0-9._-]/g, '_');

      // Save to the reports library (best-effort)
      try {
        const fd = new FormData();
        fd.append('pdfFile', blob, fileName);
        fd.append('reportName', `Regional Photo Index — ${region} — Cycle ${cycle} ${year}`);
        fd.append('reportType', 'inspection_report');
        fd.append('assetType', 'Slope');
        fd.append('description', `Regional photo index for ${region} (${inspectionTypeName || 'Inspection'}, Cycle ${cycle} ${year}) — ${slopeCount} slopes, ${photoCount} validated-defect photos.`);
        fd.append('parameters', JSON.stringify({ kind: 'region_photo_index', region, cycle: Number(cycle), year: Number(year), inspectionTypeId: Number(inspectionTypeId), slopeCount, photoCount }));
        const resp = await api.post('/reports', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (resp.data?.success) onGenerated && onGenerated();
        else setWarn('Downloaded, but saving to the reports library failed.');
      } catch (e) {
        setWarn('Downloaded, but saving to the reports library failed.');
      }

      // Download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fileName;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Region report PDF failed:', e);
      setError(e.message || 'Failed to generate the PDF.');
    } finally {
      setGen({ active: false, current: 0, total: 0 });
    }
  };

  // ---- render helpers for the on-screen report ----
  const renderHeaderRow = () => (
    <div style={{ display: 'flex', height: HEADER_H, boxSizing: 'border-box', background: '#d6ecf1', border: '1px solid #b0bec5', fontWeight: 700, fontSize: 11, color: '#37474f' }}>
      <div style={{ width: NO_W, flexShrink: 0, boxSizing: 'border-box', ...centerCell, borderRight: '1px solid #b0bec5' }}>NO</div>
      <div style={{ width: ID_W, flexShrink: 0, boxSizing: 'border-box', display: 'flex', alignItems: 'center', paddingLeft: 6, borderRight: '1px solid #b0bec5' }}>SLOPE ID</div>
      <div style={{ flex: 1, minWidth: 0, boxSizing: 'border-box', ...centerCell, borderRight: '1px solid #b0bec5' }}>PHOTOS</div>
      <div style={{ width: REM_W, flexShrink: 0, boxSizing: 'border-box', display: 'flex', alignItems: 'center', paddingLeft: 6 }}>REMARKS</div>
    </div>
  );

  const renderSlopeRow = (it) => (
    <div key={`r${it.no}`} style={{ display: 'flex', height: it.rH, boxSizing: 'border-box', borderLeft: '1px solid #cfd8dc', borderRight: '1px solid #cfd8dc', borderBottom: '1px solid #cfd8dc' }}>
      <div style={{ width: NO_W, flexShrink: 0, boxSizing: 'border-box', display: 'flex', justifyContent: 'center', paddingTop: 6, fontSize: 11, borderRight: '1px solid #eceff1' }}>{it.no}</div>
      <div style={{ width: ID_W, flexShrink: 0, boxSizing: 'border-box', padding: '6px 6px 0', borderRight: '1px solid #eceff1', fontSize: 10, fontWeight: 600, wordBreak: 'break-all', lineHeight: 1.25 }}>{it.slope.asset_code}</div>
      <div style={{ flex: 1, minWidth: 0, boxSizing: 'border-box', padding: CELL_PAD, borderRight: '1px solid #eceff1', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridAutoRows: `${CELL_H}px`, gap: PHOTO_GAP, alignContent: 'start' }}>
        {it.slope.photos.map((p) => {
          const a = photoAssets[p.photo_id];
          return (
            <div key={p.photo_id} style={{ height: CELL_H, boxSizing: 'border-box', border: '1px solid #ddd', background: '#fafafa', ...centerCell, overflow: 'hidden' }}>
              {a === undefined ? <CircularProgress size={14} />
                : a === null ? <span style={{ fontSize: 8, color: '#bbb' }}>n/a</span>
                  : <img src={a.dataUrl} alt="" style={{ maxWidth: '100%', maxHeight: '100%', display: 'block' }} />}
            </div>
          );
        })}
      </div>
      <div style={{ width: REM_W, flexShrink: 0, boxSizing: 'border-box', padding: CELL_PAD }}>
        <textarea
          value={remarks[it.slope.asset_id] ?? ''}
          onChange={(e) => setRemarks((r) => ({ ...r, [it.slope.asset_id]: e.target.value }))}
          maxLength={200}
          placeholder="Add remarks…"
          style={{ width: '100%', height: it.rH - 2 * CELL_PAD, border: 'none', outline: 'none', resize: 'none', background: 'transparent', fontSize: 10, fontFamily: 'Arial, sans-serif', color: '#333', lineHeight: 1.35 }}
        />
      </div>
    </div>
  );

  const renderCover = () => {
    const allDefects = {};
    (data?.slopes || []).forEach((s) => (s.photos || []).forEach((p) => (p.boxes || []).forEach((b) => {
      const k = b.defect_type_name || 'Defect'; allDefects[k] = (allDefects[k] || 0) + 1;
    })));
    const totalDefects = Object.values(allDefects).reduce((a, b) => a + b, 0);
    const stat = (label, val) => (
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 11, color: '#78909c' }}>{label}</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#37474f' }}>{val}</div>
      </div>
    );
    return (
      <div style={{ textAlign: 'center', color: '#263238' }}>
        <div style={{ marginTop: 120, fontSize: 34, fontWeight: 800, letterSpacing: 2 }}>PHOTO INDEX</div>
        <div style={{ marginTop: 8, fontSize: 24, fontWeight: 600 }}>{String(region).toUpperCase()} REGION</div>
        <div style={{ width: 120, height: 3, background: '#546e7a', margin: '16px auto' }} />
        <div style={{ fontSize: 16 }}>{inspectionTypeName || 'Inspection'}</div>
        <div style={{ fontSize: 15, marginTop: 4 }}>Cycle {toRoman(Number(cycle))} · {year}</div>
        <div style={{ display: 'flex', gap: 16, border: '1px solid #cfd8dc', borderRadius: 6, background: '#f8fafc', padding: 20, margin: '48px 60px 0', textAlign: 'left' }}>
          {stat('Slopes with defects', slopeCount)}
          {stat('Validated-defect photos', photoCount)}
          {stat('Total defects', totalDefects)}
        </div>
        <div style={{ marginTop: 60, fontSize: 12, color: '#90a4ae' }}>
          Generated {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} · by {user?.full_name || user?.fullName || user?.username || 'System User'}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onClose={gen.active ? undefined : onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '94vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TerrainIcon sx={{ color: theme.palette.success.main }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>Regional Photo Index — {region}</Typography>
            <Typography variant="caption" color="text.secondary">
              {inspectionTypeName || 'Inspection'} · Cycle {toRoman(Number(cycle))} · {year}
              {data ? ` · ${slopeCount} slopes · ${photoCount} photos` : ''}
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={gen.active}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, bgcolor: '#e9edf0' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
        ) : error ? (
          <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>
        ) : slopeCount === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, px: 3, bgcolor: '#fff' }}>
            <TerrainIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">No slopes with validated defects for {region} · Cycle {cycle} · {year}.</Typography>
          </Box>
        ) : (
          <>
            {!photosReady && (
              <Box sx={{ px: 2.5, py: 1 }}>
                <Typography variant="caption" color="text.secondary">Loading photos {photoLoad.done}/{photoLoad.total}…</Typography>
                <LinearProgress variant="determinate" value={photoLoad.total ? (photoLoad.done / photoLoad.total) * 100 : 0} sx={{ height: 5, borderRadius: 3, mt: 0.5 }} />
              </Box>
            )}
            {warn && <Box sx={{ px: 2.5, pt: 1 }}><Alert severity="warning" sx={{ py: 0 }}>{warn}</Alert></Box>}
            <Box sx={{ px: 1, pt: 1, pb: 2, overflowY: 'auto', maxHeight: '72vh', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <div ref={reportRef} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
                {pages.map((pg, pi) => (
                  <div key={pi} className="pdf-page-element"
                    style={{ width: PAGE_W, height: PAGE_H, boxSizing: 'border-box', padding: PAGE_PAD, background: '#fff', boxShadow: '0 1px 6px rgba(0,0,0,0.25)', overflow: 'hidden', fontFamily: 'Arial, sans-serif' }}>
                    {pg.cover ? renderCover() : (
                      <>
                        {renderHeaderRow()}
                        {pg.items.map((it, ii) => it.type === 'section'
                          ? <div key={`s${ii}`} style={{ height: SECTION_H, display: 'flex', alignItems: 'center', paddingLeft: 8, fontSize: 10, fontWeight: 700, color: '#546e7a', background: '#eceff1', borderLeft: '1px solid #cfd8dc', borderRight: '1px solid #cfd8dc' }}>Section {it.code || '—'}{it.name ? ` · ${it.name}` : ''}</div>
                          : renderSlopeRow(it))}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: `1px solid ${theme.palette.divider}`, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
        {gen.active && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary">Rendering PDF…</Typography>
              <Typography variant="caption" color="text.secondary">page {gen.current}/{gen.total}</Typography>
            </Box>
            <LinearProgress variant="determinate" value={gen.total ? (gen.current / gen.total) * 100 : 0} sx={{ height: 6, borderRadius: 3, mt: 0.5 }} />
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {slopeCount > 0 ? `${slopeCount} slopes · ${photoCount} photos · ${pages.length} pages` : ''}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} disabled={gen.active} sx={{ textTransform: 'none' }}>Close</Button>
            <Button variant="contained"
              startIcon={gen.active ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
              onClick={handleDownload}
              disabled={gen.active || loading || slopeCount === 0 || !photosReady}
              sx={{ textTransform: 'none', fontWeight: 600 }}>
              {gen.active ? 'Rendering…' : !photosReady && slopeCount > 0 ? 'Loading photos…' : 'Download Report'}
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default RegionalPhotoIndexModal;
